/**
 * AppData context — global lookup tables for the whole app.
 *
 *  - branches, quests, slots, linkedGroups — for the booking grid
 *  - leadSources                            — for the booking modal source dropdown
 *  - masterClasses                          — for the MC field in the booking modal
 *  - teaRooms                               — for the tea-room selection
 *
 * Loaded once after auth, refresh via `reload()`. Mirrors v3
 * hooks/useAppData.tsx; expects the same v3 API endpoints to be live in
 * apps/api (they are forked from v3 — see PLAN-V4.md Stage 2.0 findings).
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { api } from "../api/client";
import type {
  ApiList,
  Branch,
  Quest,
  Slot,
  LinkedGroup,
  LeadSource,
  MasterClass,
  TeaRoom,
} from "../api/types";

export interface AppData {
  branches: Branch[];
  quests: Quest[];
  slots: Slot[];
  linkedGroups: LinkedGroup[];
  leadSources: LeadSource[];
  masterClasses: MasterClass[];
  teaRooms: TeaRoom[];
  loading: boolean;
  error: string | null;
  reload: () => void;
}

const AppDataContext = createContext<AppData | null>(null);

export function AppDataProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<Omit<AppData, "reload">>({
    branches: [],
    quests: [],
    slots: [],
    linkedGroups: [],
    leadSources: [],
    masterClasses: [],
    teaRooms: [],
    loading: true,
    error: null,
  });

  const [trigger, setTrigger] = useState(0);
  const reload = () => setTrigger((n) => n + 1);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setData((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const [
          branchesRes,
          questsRes,
          slotsRes,
          linkedGroupsRes,
          leadSourcesRes,
          masterClassesRes,
          teaRoomsRes,
        ] = await Promise.all([
          api<ApiList<Branch>>("/api/branches"),
          api<ApiList<Quest>>("/api/quests"),
          api<ApiList<Slot>>("/api/slots"),
          api<ApiList<LinkedGroup>>("/api/linked-groups"),
          api<ApiList<LeadSource>>("/api/lead-sources"),
          api<ApiList<MasterClass>>("/api/master-classes"),
          api<ApiList<TeaRoom>>("/api/tea-rooms"),
        ]);

        if (cancelled) return;

        setData({
          branches: branchesRes.results ?? [],
          quests: questsRes.results ?? [],
          slots: slotsRes.results ?? [],
          linkedGroups: (linkedGroupsRes.results ?? []).map((g) => ({
            ...g,
            members: g.quests ?? g.members ?? [],
          })),
          leadSources: leadSourcesRes.results ?? [],
          masterClasses: masterClassesRes.results ?? [],
          teaRooms: teaRoomsRes.results ?? [],
          loading: false,
          error: null,
        });
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : "Ошибка загрузки данных";
        setData((prev) => ({ ...prev, loading: false, error: msg }));
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [trigger]);

  return <AppDataContext.Provider value={{ ...data, reload }}>{children}</AppDataContext.Provider>;
}

export function useAppData(): AppData {
  const ctx = useContext(AppDataContext);
  if (!ctx) throw new Error("useAppData must be used within AppDataProvider");
  return ctx;
}
